import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CountryCode } from 'src/app/models/country-code';
import countryMeta from 'src/app/data/country-meta';
import {
  AllowanceMeterItem,
  ProductRuleViolation,
} from 'src/app/models/cart-models';
import {
  AllowanceMeterGroup,
  DutyfreeAllowanceService,
} from 'src/app/services/dutyfree/dutyfree-allowance.service';
import { DutyfreeTravelCountry } from 'src/app/services/dutyfree/dutyfree-travel-country.service';

/**
 * "What you can bring in" bottom sheet, shared by the duty-free cart and the
 * product detail page. Destination editing stays in the sheet so hosts never
 * have to navigate away to change country.
 */
@Component({
  selector: 'app-dutyfree-allowance-sheet',
  templateUrl: './dutyfree-allowance-sheet.component.html',
  styleUrls: ['./dutyfree-allowance-sheet.component.scss'],
})
export class DutyfreeAllowanceSheetComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() violations: Record<string, ProductRuleViolation> = {};
  @Input() destination: DutyfreeTravelCountry | null = null;
  @Input() travellerCount = 1;
  /** Store/customs rules; rendered inside the collapsible terms section. */
  @Input() rules: string[] = [];
  @Input() isLoading = false;
  /** Highlight the category this product counts toward, when opened from a product. */
  @Input() focusCategory = '';
  /**
   * When true (arrival locations), the destination country is fixed to the
   * airport's own country and the user cannot change it.
   */
  @Input() destinationLocked = false;
  /** True when the bag already has line items (vs truly empty). */
  @Input() hasBagItems = false;

  @Output() closed = new EventEmitter<void>();
  @Output() destinationSelected = new EventEmitter<CountryCode>();
  @Output() travellerCountChange = new EventEmitter<number>();

  groups: AllowanceMeterGroup[] = [];
  termsOpen = false;
  editingDestination = false;
  destSearchQuery = '';
  filteredDestCountries: CountryCode[] = [];
  popularDestCountries: CountryCode[] = [];
  skeletonMeterItems = [0, 1, 2, 3];

  constructor(
    private allowance: DutyfreeAllowanceService,
    private translate: TranslateService
  ) {
    const codes = ['GB', 'AE', 'US', 'IN'];
    this.popularDestCountries = codes
      .map((code) => countryMeta.find((c) => c.code === code))
      .filter(Boolean) as CountryCode[];
  }

  get maxTravellerCount(): number {
    return this.allowance.maxTravellerCount;
  }

  get travellerLabel(): string {
    return this.allowance.travellerLabel(this.travellerCount);
  }

  get hasDestination(): boolean {
    return !!(this.destination?.countryCode || this.destination?.countryName);
  }

  get hasMeters(): boolean {
    return this.groups.length > 0;
  }

  get showEmptyState(): boolean {
    return this.showMeters && !this.hasMeters;
  }

  /** Bag has items, but this destination has no customs meter data. */
  get showUnavailableState(): boolean {
    return this.showEmptyState && this.hasBagItems;
  }

  get emptyStateIcon(): string {
    return this.showUnavailableState
      ? 'globe-outline'
      : 'bag-handle-outline';
  }

  get emptyStateTitle(): string {
    if (this.showUnavailableState) {
      return this.translate.instant(
        'dutyfree.retail_cart.allowance_unavailable_title'
      );
    }
    return this.translate.instant('dutyfree.retail_cart.allowance_none_yet');
  }

  get emptyStateHint(): string {
    if (this.showUnavailableState) {
      const country =
        this.destination?.countryName ||
        this.destination?.countryCode ||
        '';
      return this.translate.instant(
        'dutyfree.retail_cart.allowance_unavailable_hint',
        { country }
      );
    }
    return this.translate.instant(
      'dutyfree.retail_cart.allowance_empty_sheet_hint'
    );
  }

  get showDestinationPicker(): boolean {
    if (this.destinationLocked) {
      return false;
    }
    return !this.isLoading && (!this.hasDestination || this.editingDestination);
  }

  get showMeters(): boolean {
    if (this.destinationLocked) {
      return !this.isLoading && this.hasDestination;
    }
    return !this.isLoading && this.hasDestination && !this.editingDestination;
  }

  get termsCount(): number {
    return (this.rules || []).length + 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.violations || changes.travellerCount) {
      this.groups = this.allowance.getMeterGroups(
        this.allowance.normalizeViolations(this.violations),
        this.travellerCount
      );
    }
    if (changes.isOpen && this.isOpen) {
      this.termsOpen = false;
      this.resetDestinationEditor();
    }
  }

  close(): void {
    this.resetDestinationEditor();
    this.closed.emit();
  }

  onDone(): void {
    if (this.editingDestination && this.hasDestination) {
      this.resetDestinationEditor();
      return;
    }
    this.close();
  }

  onChangeDestination(event?: Event): void {
    event?.stopPropagation();
    if (this.destinationLocked) {
      return;
    }
    this.editingDestination = true;
    this.destSearchQuery = '';
    this.filteredDestCountries = [];
  }

  filterDestCountries(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.destSearchQuery = value;
    const query = value.trim().toLowerCase();
    if (!query) {
      this.filteredDestCountries = [];
      return;
    }
    this.filteredDestCountries = countryMeta
      .filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.code.toLowerCase().includes(query)
      )
      .slice(0, 8);
  }

  selectDestination(country: CountryCode): void {
    if (this.destinationLocked) {
      return;
    }
    this.resetDestinationEditor();
    this.destinationSelected.emit(country);
  }

  changeTravellers(delta: number): void {
    const next = Math.min(
      this.maxTravellerCount,
      Math.max(1, (this.travellerCount || 1) + delta)
    );
    if (next !== this.travellerCount) {
      this.travellerCountChange.emit(next);
    }
  }

  toggleTerms(): void {
    this.termsOpen = !this.termsOpen;
  }

  isFocused(item: AllowanceMeterItem): boolean {
    const focus = String(this.focusCategory || '')
      .trim()
      .toLowerCase();
    if (!focus) {
      return false;
    }
    return String(item?.category || '').trim().toLowerCase() === focus;
  }

  categoryLabel(item: AllowanceMeterItem): string {
    return this.allowance.formatCategoryLabel(item.category);
  }

  usedText(item: AllowanceMeterItem): string {
    return this.allowance.formatUsedText(item);
  }

  allowedText(item: AllowanceMeterItem): string {
    return this.allowance.formatAllowedText(item);
  }

  progress(item: AllowanceMeterItem): number {
    return this.allowance.getProgress(item);
  }

  limitPercent(item: AllowanceMeterItem): number {
    return this.allowance.getLimitPercent(item);
  }

  state(item: AllowanceMeterItem): string {
    return this.allowance.getState(item);
  }

  tagLabel(item: AllowanceMeterItem): string {
    return this.allowance.getTagLabel(item);
  }

  note(item: AllowanceMeterItem): string {
    return this.allowance.getNote(item);
  }

  get subtitle(): string {
    if (this.showDestinationPicker) {
      return this.translate.instant(
        'dutyfree.retail_cart.allowance_set_destination'
      );
    }
    return this.translate.instant('dutyfree.retail_cart.allowance_sheet_sub');
  }

  private resetDestinationEditor(): void {
    this.editingDestination = false;
    this.destSearchQuery = '';
    this.filteredDestCountries = [];
  }
}
